<template>
  <div class="order-or-shipment-template-option">
    <div class="flex flex-gap-1 align-items-center">
      <span>{{ template.name }}</span>

      <RygenChip
        v-if="isDefaultTemplate(template)"
        :label="t('common.default')"
        :severity="Severity.INFO_LIGHT"
      />
    </div>

    <template v-if="!readonly">
      <RygenButton
        v-if="isDefaultTemplate(template)"
        mode="tertiary"
        icon="pi pi-star-fill"
        :aria-label="t('common.removeAsDefault')"
        :disabled="readonly"
        @mousedown.stop="emit('remove-as-default', template)"
      />
      <RygenButton
        v-else
        mode="tertiary"
        icon="pi pi-star"
        :aria-label="t('common.setAsDefault')"
        :disabled="readonly"
        @mousedown.stop="emit('save-as-default', template)"
      />
    </template>
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { Severity } from '@rygen/rygen-ui/model';

import { Order, OrderOrShipmentTemplate } from '@/model';
import { useParentEntityStore } from '@/stores/ParentEntity';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import RygenChip from '@rygen/rygen-ui/components/ui/RygenChip.vue';

const props = defineProps({
  template: {
    type: Object as PropType<OrderOrShipmentTemplate>,
    required: true,
  },
  readonly: {
    type: Boolean,
    default: () => false,
  },
});
const { template, readonly } = toRefs(props);
const emit = defineEmits<{
  (event: 'save-as-default', template: OrderOrShipmentTemplate): void;
  (event: 'remove-as-default', template: OrderOrShipmentTemplate): void;
}>();

const { t } = useI18n();
const { parentEntity } = useParentEntityStore();

const isDefaultTemplate = (template: OrderOrShipmentTemplate) => {
  return parentEntity.value instanceof Order
    ? template?.defaultOrderTemplate
    : template?.defaultShipmentTemplate;
};
</script>

<style>
.order-or-shipment-template-option {
  display: flex;
  gap: var(--gap-1);
  justify-content: space-between;
  align-items: center;
  width: 100%;

  &:hover {
    .p-button-icon-only:has(.pi-star, .pi-star-fill) {
      opacity: 1;
    }
  }

  .p-button-icon-only:has(.pi-star, .pi-star-fill) {
    font-size: 12.25px;
    opacity: 0;
    transition: opacity 100ms ease-in;
  }
}
</style>
